import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Volume2, BookOpen, Sparkles, Loader2,
  CheckCircle2, X, Trophy, RotateCcw, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { BlurReveal } from "@/components/effects/blur-reveal";
import { AmbientCanvas } from "@/components/effects/ambient-canvas";
import { PageFrame } from "@/components/page-frame";
import { AppTopBar } from "@/components/app-topbar";
import {
  fetchTopics, fetchTopicInfo, fetchTopicVocab, fetchTopicMastery,
  saveProgress, submitQuizSession,
  nextTopicAfter, setRecentTopicId, getRecentTopicId,
  type Topic, type VocabWord, type TopicMastery
} from "@/lib/queries";
import { playWordClick, speakWord } from "@/lib/audio";
import { playCorrect, playWrong, playReveal, playSessionEnd } from "@/lib/sounds";
import { flyStarsToPill, confettiForResult } from "@/lib/effects";
import { cn } from "@/lib/utils";

/**
 * Legacy interleaved Learn flow (mirrors js/learn.js v55):
 *
 *   flashcard 1 → micro-test 1 → flashcard 2 → micro-test 2 → …
 *   → flashcard N → micro-test N → cumulative 5-Q test → results
 *
 * Micro-tests do NOT touch the score or award points; they're just
 * for retention. A wrong micro-test answer returns you to the same
 * flashcard. The cumulative test at the end is where points come
 * from (one submit_quiz_session call with mode='learn').
 */

const PRACTICE_LEARN = 5;     // flashcards per session
const PRACTICE_TEST  = 5;     // cumulative MCQs after flashcards
const MIN_VOCAB      = 6;
const POINTS_PER_CORRECT = 10;
const NEXT_DELAY_MS  = 900;

type Phase =
  | "loading"
  | "no-topics"
  | "browse"
  | "no-vocab"
  | "flashcard"      // showing a word; "Test me on this" → micro-test
  | "micro-test"     // one MCQ for the current flashcard word
  | "cumulative"     // final 5-Q test across the practised set
  | "results";

type Direction = "ar-en" | "en-ar";
type Option = { id: string; label: string; lang: "en" | "ar"; isCorrect: boolean };
type Question = { word: VocabWord; direction: Direction; options: Option[] };

export default function Learn() {
  const [params] = useSearchParams();
  const urlTopic = params.get("topic");
  const nav = useNavigate();

  const [phase, setPhase] = React.useState<Phase>("loading");
  const [topic, setTopic] = React.useState<Topic | null>(null);
  const [vocab, setVocab] = React.useState<VocabWord[]>([]);
  const [topics, setTopics] = React.useState<Topic[]>([]);
  const [mastery, setMastery] = React.useState<Map<string, TopicMastery>>(new Map());

  // Session state.
  const [practiceSet, setPracticeSet] = React.useState<VocabWord[]>([]);
  const [cardIdx, setCardIdx]   = React.useState(0); // interleaved flashcard pointer
  const [cumIdx, setCumIdx]     = React.useState(0); // cumulative test pointer
  const [question, setQ]        = React.useState<Question | null>(null);
  const [picked, setPicked]     = React.useState<string | null>(null);

  // Score — ONLY incremented during cumulative test.
  const [correct, setCorrect]   = React.useState(0);
  const [wrongHistory, setWrongHistory] = React.useState<VocabWord[]>([]);

  const startRef = React.useRef<Date>(new Date());
  const [submitting, setSubmitting] = React.useState(false);
  const [awarded, setAwarded] = React.useState<{ new_total: number; points_awarded: number } | null>(null);

  /* ─────────────── Init ─────────────── */
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setPhase("loading");
      const [allTopics, mast] = await Promise.all([fetchTopics(), fetchTopicMastery()]);
      if (cancelled) return;
      setTopics(allTopics);
      setMastery(mast);

      // No topic → browse panel.
      if (!urlTopic) {
        setPhase(allTopics.length === 0 ? "no-topics" : "browse");
        return;
      }

      const resolved = await fetchTopicInfo(urlTopic);
      if (!resolved) { setPhase("no-topics"); return; }
      setRecentTopicId(resolved.id);
      const words = await fetchTopicVocab(resolved.id);
      if (cancelled) return;
      setTopic(resolved);
      setVocab(words);
      if (words.length < MIN_VOCAB) { setPhase("no-vocab"); return; }

      const picks = shuffle([...words]).slice(0, Math.min(PRACTICE_LEARN, words.length));
      setPracticeSet(picks);
      setCardIdx(0);
      setCumIdx(0);
      setCorrect(0);
      setWrongHistory([]);
      startRef.current = new Date();
      setPhase("flashcard");
      // Auto-speak the first flashcard.
      if (picks[0]) void speakWord(picks[0]);
    })();
    return () => { cancelled = true; };
  }, [urlTopic]);

  /* ─────────────── Phase transitions ─────────────── */

  function startMicroTest() {
    const w = practiceSet[cardIdx];
    if (!w) return;
    const q = buildQuestion(vocab, w);
    setQ(q);
    setPicked(null);
    setPhase("micro-test");
    playReveal();
    if (q.direction === "ar-en") void speakWord(q.word);
  }

  function startCumulative() {
    if (practiceSet.length === 0) { void endSession(); return; }
    setCumIdx(0);
    const focus = practiceSet[0];
    const q = buildQuestion(vocab, focus);
    setQ(q);
    setPicked(null);
    setPhase("cumulative");
    playReveal();
    if (q.direction === "ar-en") void speakWord(q.word);
  }

  async function endSession() {
    setPhase("results");
    setSubmitting(true);
    playSessionEnd(correct, PRACTICE_TEST);
    window.setTimeout(() => {
      const card = document.querySelector("[data-results-card]");
      confettiForResult({ correct, total: PRACTICE_TEST, originEl: card });
    }, 80);
    try {
      const r = await submitQuizSession({
        claimed_correct: correct,
        session_started: startRef.current,
        mode:            "learn"
      });
      if (r) setAwarded(r);
    } catch (e) { console.warn("[learn] submit", e); }
    setSubmitting(false);
  }

  function onMicroAnswer(opt: Option, btn: HTMLElement | null) {
    if (picked) return;
    setPicked(opt.id);
    const w = question?.word;
    if (w) {
      void saveProgress({ word_id: w.id, correct: opt.isCorrect, mode: "learn", points: 0 });
    }
    if (opt.isCorrect) playCorrect();
    else                playWrong();

    window.setTimeout(() => {
      if (opt.isCorrect) {
        const ni = cardIdx + 1;
        if (ni >= practiceSet.length) {
          startCumulative();
        } else {
          setCardIdx(ni);
          setPicked(null);
          setPhase("flashcard");
          if (practiceSet[ni]) void speakWord(practiceSet[ni]);
        }
      } else {
        setPicked(null);
        setPhase("flashcard");
        if (w) void speakWord(w);
      }
    }, NEXT_DELAY_MS);
    // Quietly use `btn` so eslint doesn't complain when stars are skipped here.
    void btn;
  }

  function onCumulativeAnswer(opt: Option, btn: HTMLElement | null) {
    if (picked) return;
    setPicked(opt.id);
    const w = question?.word;
    if (w) {
      void saveProgress({
        word_id: w.id, correct: opt.isCorrect, mode: "learn",
        points:  opt.isCorrect ? POINTS_PER_CORRECT : 0
      });
    }
    if (opt.isCorrect) {
      setCorrect(c => c + 1);
      playCorrect();
      flyStarsToPill(btn);
    } else {
      playWrong();
      if (w) setWrongHistory(prev => [...prev, w]);
    }

    window.setTimeout(() => {
      const ni = cumIdx + 1;
      if (ni >= PRACTICE_TEST) {
        void endSession();
      } else {
        setCumIdx(ni);
        const focus = practiceSet[ni % practiceSet.length];
        const q = buildQuestion(vocab, focus);
        setQ(q);
        setPicked(null);
        playReveal();
        if (q.direction === "ar-en") void speakWord(q.word);
      }
    }, NEXT_DELAY_MS);
  }

  function goNextTopic() {
    if (!topic) return;
    const next = nextTopicAfter(topic.id, topics);
    if (next) {
      setRecentTopicId(next.id);
      nav(`/learn?topic=${encodeURIComponent(next.id)}`);
    }
  }

  /* ─────────────── Render helpers ─────────────── */

  const learnCard    = practiceSet[cardIdx];
  const nextTopic    = topic ? nextTopicAfter(topic.id, topics) : null;
  const topicProgress = topic ? mastery.get(topic.id) : null;
  // 0..1 progress through the whole interleaved session.
  const totalSteps    = practiceSet.length + PRACTICE_TEST;
  const stepsDone     = phase === "cumulative" ? practiceSet.length + cumIdx
                       : phase === "results"    ? totalSteps
                       : cardIdx;

  return (
    <PageFrame surface="app">
      <AppTopBar />
      <div className="relative">
        <AmbientCanvas className="absolute inset-0 -z-10 w-full h-[900px] pointer-events-none" density={40} />

        <div className="relative mx-auto max-w-3xl px-4 md:px-6 pt-10 pb-32">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/dashboard"><ArrowLeft className="w-4 h-4" /> Back</Link>
            </Button>
            {phase === "flashcard" && <Badge variant="accent">Card {cardIdx + 1} / {practiceSet.length}</Badge>}
            {phase === "micro-test" && <Badge variant="default" className="border-accent-lo bg-accent/10 text-accent">Retention check</Badge>}
            {phase === "cumulative" && <Badge variant="accent">Final test · Q {cumIdx + 1} / {PRACTICE_TEST}</Badge>}
          </div>

          <BlurReveal>
            <div className="flex items-center gap-3 mb-2">
              <BookOpen className="w-6 h-6 text-accent" />
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                {phase === "browse" ? "All levels" : "Learn"}
              </h1>
              {phase === "browse" && topics.length > 0 && (
                <Badge variant="outline" className="ml-auto">{topics.length} topics</Badge>
              )}
            </div>
            {phase === "browse" ? (
              <p className="text-fg-2">Pick a topic to learn. Your current topic is highlighted.</p>
            ) : topic && (
              <div className="flex flex-wrap items-baseline gap-3">
                <p className="text-fg-2">
                  <span className="text-accent">{topic.name_en}</span>
                  <span className="text-fg-3"> · {vocab.length} words</span>
                </p>
                {topicProgress && (
                  <span className="text-xs text-fg-3">
                    Mastered <span className="text-fg-2 font-semibold">{topicProgress.correct}/{topicProgress.total}</span> ({topicProgress.pct}%)
                  </span>
                )}
              </div>
            )}
          </BlurReveal>

          {(phase === "flashcard" || phase === "micro-test" || phase === "cumulative") && (
            <div className="mt-4 h-1.5 rounded-full bg-bg-2 overflow-hidden">
              <div className="h-full bg-accent transition-all" style={{ width: `${(stepsDone / totalSteps) * 100}%` }} />
            </div>
          )}

          <div className="mt-8">
            {phase === "loading" && <Spinner />}

            {phase === "no-topics" && (
              <Card className="text-center"><CardContent className="py-12"><p className="text-fg-2">No topics yet.</p></CardContent></Card>
            )}

            {phase === "browse" && <BrowsePanel topics={topics} mastery={mastery} />}

            {phase === "no-vocab" && (
              <Card className="text-center"><CardContent className="py-12">
                <p className="text-fg-2">This topic has only {vocab.length} words — need at least {MIN_VOCAB}.</p>
                {nextTopic && <Button className="mt-4" onClick={goNextTopic}>Next topic: {nextTopic.name_en} <ChevronRight className="w-4 h-4" /></Button>}
              </CardContent></Card>
            )}

            {phase === "flashcard" && learnCard && (
              <FlashcardView
                word={learnCard}
                last={cardIdx + 1 === practiceSet.length}
                onReady={startMicroTest}
              />
            )}

            {phase === "micro-test" && question && (
              <BlurReveal>
                <p className="mb-3 text-center text-xs text-fg-3">
                  Points come from the final test below — this is just to make sure it stuck.
                </p>
                <QuestionCard question={question} picked={picked} onPick={onMicroAnswer} variant="micro" />
              </BlurReveal>
            )}

            {phase === "cumulative" && question && (
              <QuestionCard question={question} picked={picked} onPick={onCumulativeAnswer} variant="final" />
            )}

            {phase === "results" && (
              <ResultsCard
                correct={correct}
                total={PRACTICE_TEST}
                submitting={submitting}
                awarded={awarded}
                wrongHistory={wrongHistory}
                onRetry={() => nav(0)}
                onHome={() => nav("/dashboard")}
                onNextTopic={nextTopic ? goNextTopic : null}
                nextTopicName={nextTopic?.name_en}
              />
            )}
          </div>
        </div>
      </div>
    </PageFrame>
  );
}

/* ─────────────── Subcomponents ─────────────── */

function FlashcardView({ word, last, onReady }: { word: VocabWord; last: boolean; onReady: () => void }) {
  return (
    <BlurReveal>
      <Card className="text-center relative overflow-hidden">
        <div aria-hidden className="absolute -top-12 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full bg-accent/15 blur-3xl" />
        <CardContent className="relative py-12">
          <div className="text-xs uppercase tracking-widest text-fg-3 mb-1">Flashcard</div>
          <div className="mt-6 text-6xl md:text-8xl font-bold text-accent break-words" dir="rtl" style={{ fontFamily: "'Noto Sans Arabic',serif" }}>
            {word.arabic}
          </div>
          {word.transliteration && <div className="mt-3 text-xl text-fg-2">{word.transliteration}</div>}
          <div className="mt-1 text-lg font-medium">{word.english}</div>
          <Button variant="outline" size="md" className="mt-6" onClick={() => playWordClick(word)}>
            <Volume2 className="w-4 h-4" /> Play audio
          </Button>
          <div className="mt-8 flex gap-3 justify-center">
            <Button size="lg" onClick={onReady}>
              {last ? <>Got it — final test <Sparkles className="w-4 h-4" /></> : <>Got it — test me <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </BlurReveal>
  );
}

function QuestionCard({ question, picked, onPick, variant }: {
  question: Question; picked: string | null; onPick: (opt: Option, btn: HTMLElement | null) => void; variant: "micro" | "final";
}) {
  const w = question.word;
  return (
    <BlurReveal>
      <Card className={cn("text-center", variant === "micro" && "border-accent-lo")}>
        <CardContent className="py-10">
          <div className="text-xs uppercase tracking-widest text-fg-3">
            {question.direction === "ar-en" ? "What does this mean?" : "What's the Arabic for…"}
          </div>
          <div className={cn(
            "mt-5 font-bold text-accent",
            question.direction === "ar-en" ? "text-6xl md:text-7xl" : "text-4xl md:text-5xl"
          )} style={question.direction === "ar-en"
            ? { fontFamily: "'Noto Sans Arabic',serif", direction: "rtl" }
            : undefined}>
            {question.direction === "ar-en" ? w.arabic : w.english}
          </div>
          {question.direction === "ar-en" && w.transliteration && (
            <div className="mt-2 text-lg text-fg-2">{w.transliteration}</div>
          )}
          {question.direction === "ar-en" && (
            <Button variant="ghost" size="sm" className="mt-3" onClick={() => playWordClick(w)}>
              <Volume2 className="w-4 h-4" /> Listen
            </Button>
          )}

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {question.options.map((opt, i) => {
              const isPicked = picked === opt.id;
              const showRight = picked !== null && opt.isCorrect;
              const showWrong = isPicked && !opt.isCorrect;
              return (
                <button key={opt.id} type="button"
                  disabled={picked !== null}
                  onClick={e => onPick(opt, e.currentTarget)}
                  className={cn(
                    "rounded-2xl border px-5 py-4 text-left transition-colors flex items-start gap-3",
                    !picked && "border-line bg-bg hover:bg-bg-3 hover:border-line-2",
                    showRight && "border-ok/40 bg-ok/10 text-ok",
                    showWrong && "border-bad/40 bg-bad/10 text-bad",
                    picked && !showRight && !showWrong && "border-line bg-bg-3/30 text-fg-2 opacity-60"
                  )}
                  style={opt.lang === "ar" ? { fontFamily: "'Noto Sans Arabic',serif", direction: "rtl" } : undefined}>
                  <span className="text-xs uppercase tracking-widest text-fg-3 shrink-0">{String.fromCharCode(65 + i)}</span>
                  <span className={cn("flex-1", opt.lang === "ar" && "text-lg")}>{opt.label}</span>
                  {showRight && <CheckCircle2 className="w-4 h-4 shrink-0" />}
                  {showWrong && <X className="w-4 h-4 shrink-0" />}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </BlurReveal>
  );
}

function ResultsCard({ correct, total, submitting, awarded, wrongHistory, onRetry, onHome, onNextTopic, nextTopicName }: {
  correct: number; total: number; submitting: boolean;
  awarded: { new_total: number; points_awarded: number } | null;
  wrongHistory: VocabWord[];
  onRetry: () => void; onHome: () => void;
  onNextTopic: (() => void) | null; nextTopicName?: string;
}) {
  const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
  const stars = pct >= 80 ? 3 : pct >= 50 ? 2 : pct > 0 ? 1 : 0;
  const mastered = pct >= 80;
  return (
    <BlurReveal>
      <Card data-results-card>
        <CardContent className="text-center py-10">
          <div className="flex justify-center gap-1 mb-4 text-2xl">
            {Array.from({ length: 3 }).map((_, i) => (
              <span key={i} className={i < stars ? "text-accent" : "text-fg-3 opacity-30"}>★</span>
            ))}
          </div>
          <div className="text-xs uppercase tracking-widest text-fg-3">Round complete</div>
          <h2 className="text-4xl font-bold tracking-tight mt-2">
            {correct} / {total} <span className="text-fg-3 font-normal">correct</span>
          </h2>

          {submitting ? (
            <p className="mt-4 text-fg-2 inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-accent" /> Banking points…
            </p>
          ) : awarded ? (
            <div className="mt-5 inline-flex items-center gap-3 rounded-2xl border border-accent-lo bg-accent/10 px-5 py-3">
              <Trophy className="w-5 h-5 text-accent" />
              <span className="text-accent font-bold text-xl">+{awarded.points_awarded.toLocaleString()}</span>
              <span className="text-xs uppercase tracking-widest text-fg-3">points</span>
              <span className="text-xs text-fg-2">· new total {awarded.new_total.toLocaleString()}</span>
            </div>
          ) : (
            <p className="mt-4 text-fg-2 text-sm">Points couldn't sync — try again in a moment.</p>
          )}

          {wrongHistory.length > 0 && (
            <div className="mt-7 text-left">
              <h3 className="text-xs uppercase tracking-widest text-fg-3 mb-2">Words to revisit</h3>
              <ul className="rounded-2xl border border-line bg-bg p-2 divide-y divide-line">
                {wrongHistory.map(w => (
                  <li key={w.id} className="flex items-center gap-3 px-3 py-2">
                    <span className="text-lg text-accent" style={{ fontFamily: "'Noto Sans Arabic',serif" }}>{w.arabic}</span>
                    <span className="text-xs text-fg-3 flex-1">{w.transliteration}</span>
                    <span className="text-sm">{w.english}</span>
                    <button type="button" onClick={() => playWordClick(w)} className="text-fg-3 hover:text-fg">
                      <Volume2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            {mastered && onNextTopic ? (
              <>
                <Button size="lg" onClick={onNextTopic}>
                  Next topic{nextTopicName ? `: ${nextTopicName}` : ""} <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="lg" onClick={onRetry}><RotateCcw className="w-4 h-4" /> Try again</Button>
              </>
            ) : (
              <>
                <Button size="lg" onClick={onRetry}><RotateCcw className="w-4 h-4" /> Try again</Button>
                {onNextTopic && (
                  <Button variant="outline" size="lg" onClick={onNextTopic}>Skip to next <ChevronRight className="w-4 h-4" /></Button>
                )}
                <Button variant="ghost" size="lg" onClick={onHome}>Dashboard <ArrowRight className="w-4 h-4" /></Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </BlurReveal>
  );
}

function BrowsePanel({ topics, mastery }: { topics: Topic[]; mastery: Map<string, TopicMastery> }) {
  const recentId = getRecentTopicId();
  return (
    <BlurReveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {topics.map((t, i) => {
          const m = mastery.get(t.id);
          const pct = m?.pct ?? 0;
          const isCurrent = t.id === recentId;
          const isDone = pct >= 80;
          return (
            <Link
              key={t.id}
              to={`/learn?topic=${encodeURIComponent(t.id)}`}
              onClick={() => setRecentTopicId(t.id)}
              className={
                "rounded-2xl border bg-bg-2/70 backdrop-blur-sm p-4 hover:border-line-2 hover:bg-bg-3 transition-colors group flex flex-col min-h-[150px] " +
                (isCurrent ? "border-accent ring-1 ring-accent/40"
                  : isDone   ? "border-ok/40"
                  : "border-line")
              }>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-lg bg-bg border border-line text-xs font-bold text-fg-2 tabular-nums shrink-0">
                  {i + 1}
                </span>
                <div className="flex-1 flex items-center gap-1.5 min-w-0">
                  {isCurrent && <Badge variant="accent" className="text-[9px] px-1.5 py-0.5">Current</Badge>}
                  {isDone && !isCurrent && <span className="text-ok text-xs">✓</span>}
                </div>
                <span className="text-fg-3 group-hover:text-fg transition-colors shrink-0">→</span>
              </div>
              <div className="text-2xl md:text-[1.7rem] leading-tight text-accent text-right break-words"
                   dir="rtl"
                   style={{ fontFamily: "'Noto Sans Arabic',serif" }}>
                {t.name_ar}
              </div>
              <div className="mt-1.5 text-sm font-semibold text-fg truncate">{t.name_en}</div>
              <div className="text-xs text-fg-3">{m?.total ?? 0} words · {m?.correct ?? 0} mastered</div>
              <div className="mt-auto pt-3 flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-bg overflow-hidden">
                  <div
                    className={"h-full transition-all duration-500 " + (isDone ? "bg-ok" : "bg-accent")}
                    style={{ width: `${pct}%` }} />
                </div>
                <span className="text-[10px] uppercase tracking-widest text-fg-3 tabular-nums">{pct}%</span>
              </div>
            </Link>
          );
        })}
      </div>
    </BlurReveal>
  );
}

function Spinner() {
  return (
    <div className="py-16 flex items-center justify-center gap-2 text-fg-3">
      <Loader2 className="w-4 h-4 animate-spin text-accent" /> Loading…
    </div>
  );
}

/* ─────────────── helpers ─────────────── */

function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildQuestion(vocab: VocabWord[], focus: VocabWord): Question {
  const direction: Direction = Math.random() < 0.5 ? "ar-en" : "en-ar";
  const lang: "en" | "ar" = direction === "ar-en" ? "en" : "ar";
  const field = direction === "ar-en" ? "english" : "arabic";
  const distractors = shuffle(vocab.filter(w => w.id !== focus.id && (w as any)[field] !== (focus as any)[field])).slice(0, 3);
  const all = [focus, ...distractors];
  const options: Option[] = shuffle(all.map(w => ({
    id:        String(w.id),
    label:     (w as any)[field] || "—",
    lang,
    isCorrect: w.id === focus.id
  })));
  return { word: focus, direction, options };
}
